import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DateTimeDisplay, Breadcrumbs, SearchQuery, StatusMessage } from '../../components/ui';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import BureauFilter from '../../components/filters/BureauFilter';
import { FilterBtn } from '../../components/ui/Buttons';
import { BureauCategoryTable } from '../../components/alarms/category/views/BureauCategoryTable';
import Pagination from '../../components/ui/Pagination';
import { getBureauCategoryLabel } from '../../components/alarms/category/constants';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { useBureauSignalRContext } from '../../contexts/BureauSignalRContext';
import type { BureauAlarm } from '../../services/bureauManagement/bureauManagement.types';
import { getBureauAlarmsByCategory } from '../../services/bureauManagement/bureauManagement.service';
import { fetchCshGroup, fetchCshGroupAlarms } from '../../services/csh/csh.service';
import type { CshGroupAlarm, CshGroupDetail } from '../../services/csh/csh.types';
import { TOAST_SUCCESS_MS } from '../../constants/timeouts';
import { alarmAcknowledgedEvent } from '../../services/events/alarmEvents';

type AlarmCategory = 'all' | 'active-not-ack' | 'active-ack' | 'not-active-not-ack';

const PAGE_SIZE = 100;

const toBool = (value: unknown): boolean => value === true;

const isActiveAlarm = (alarm: BureauAlarm): boolean => {
  const sourceState = String(alarm.sourceAlarmState || '').trim().toLowerCase();
  if (sourceState) {
    return sourceState === 'active';
  }

	const state = String(alarm.bureauAlarmState || '').toLowerCase();
	if (state.includes('active') && !state.includes('notactive') && !state.includes('inactive')) {
		return true;
	}
	if (typeof alarm.alarmState === 'number') {
		return alarm.alarmState === 1;
	}
	return false;
};

const normalizeAlarm = (alarm: CshGroupAlarm): BureauAlarm => {
	return {
		id: alarm.id,
		bureauAlarmId: alarm.bureauAlarmId ?? alarm.id,
		siteRegistryId: alarm.siteIdentifierRegistryId,
		siteGuid: alarm.siteGuid ?? undefined,
		siteIdentifier: alarm.siteIdentifier ?? undefined,
		siteName: alarm.siteName ?? undefined,
		localAck: toBool(alarm.localAck),
		alarmState: alarm.alarmState,
		bureauAlarmState: alarm.bureauAlarmState ?? null,
		firstOccurrenceAt: alarm.firstOccurrenceAt ?? null,
		lastOccurrenceAt: alarm.lastOccurrenceAt ?? null,
		createdAt: alarm.createdAt ?? null,
	};
};

const getAlarmSiteId = (alarm: BureauAlarm): number | null => {
	const withVariants = alarm as BureauAlarm & {
		sirId?: number | null;
		siteIdentifierRegistryId?: number | null;
		siteId?: number | null;
	};
	const candidate =
		withVariants.siteRegistryId ??
		withVariants.sirId ??
		withVariants.siteIdentifierRegistryId ??
		withVariants.siteId ??
		null;
	const numeric = Number(candidate);
	if (!Number.isFinite(numeric) || numeric <= 0) {
		return null;
	}
	return numeric;
};

const toLower = (value: unknown): string => String(value || '').trim().toLowerCase();

const CSHDetails: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { subscribe } = useBureauSignalRContext();

	const [group, setGroup] = useState<CshGroupDetail | null>(null);
	const [alarms, setAlarms] = useState<BureauAlarm[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
	const [selectedCategory, setSelectedCategory] = useState<AlarmCategory>('active-not-ack');
	const [page, setPage] = useState<number>(1);
	const [sortColumn, setSortColumn] = useState<string>('last');
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
	const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [refreshTick, setRefreshTick] = useState<number>(0);

	const breadcrumbs = useBreadcrumbs({ currentPageTitle: group?.name || 'CSH Details' });

	useEffect(() => {
		setPage(1);
	}, [searchQuery]);

	useEffect(() => {
		if (!id) {
			setError('Missing CSH group id');
			setLoading(false);
			return;
		}

		let mounted = true;
		const fetchData = async () => {
			setLoading(true);
			setError(null);
			try {
				const [groupDetail, groupAlarms, allAlarmsResponse] = await Promise.all([
					fetchCshGroup(id),
					fetchCshGroupAlarms(id),
					getBureauAlarmsByCategory('all', 1, 100, true),
				]);

				if (!mounted) {return;}
				if (!groupDetail) {
					setError('CSH group not found');
					setGroup(null);
					setAlarms([]);
					return;
				}

				const allowedSiteIds = new Set(
					(groupDetail.sites || [])
						.map((s) => Number(s.siteIdentifierRegistryId))
						.filter((v) => Number.isFinite(v) && v > 0)
				);
				const allowedSiteGuids = new Set(
					(groupDetail.sites || [])
						.map((s) => toLower(s.siteGuid))
						.filter((v) => v.length > 0)
				);
				const allowedSiteIdentifiers = new Set(
					(groupDetail.sites || [])
						.map((s) => toLower(s.siteIdentifier))
						.filter((v) => v.length > 0)
				);

				const belongsToGroup = (alarm: BureauAlarm): boolean => {
					if (allowedSiteIds.size === 0 && allowedSiteGuids.size === 0 && allowedSiteIdentifiers.size === 0) {
						return true;
					}

					const siteId = getAlarmSiteId(alarm);
					if (siteId !== null && allowedSiteIds.has(siteId)) {
						return true;
					}

					const guid = toLower(alarm.siteGuid);
					if (guid && allowedSiteGuids.has(guid)) {
						return true;
					}

					const identifier = toLower(alarm.siteIdentifier);
					if (identifier && allowedSiteIdentifiers.has(identifier)) {
						return true;
					}

					return false;
				};

				const normalizedGroupAlarms = (Array.isArray(groupAlarms) ? groupAlarms : [])
					.map(normalizeAlarm)
					.filter((a) => belongsToGroup(a));

				const allAlarmsFromApi =
					allAlarmsResponse?.data?.alarmsAll ||
					allAlarmsResponse?.data?.bureauAlarmsAll ||
					allAlarmsResponse?.data?.alarms ||
					allAlarmsResponse?.data?.bureauAlarms ||
					[];

				const fallbackAlarms = (Array.isArray(allAlarmsFromApi) ? allAlarmsFromApi : [])
					.filter((a) => belongsToGroup(a));

				const finalAlarms = normalizedGroupAlarms.length > 0 ? normalizedGroupAlarms : fallbackAlarms;

				setGroup(groupDetail);
				setAlarms(finalAlarms);
				setPage(1);
				setSelectedIds({});
			} catch (e) {
				if (!mounted) {return;}
				setError('Failed to load CSH details');
			} finally {
				if (mounted) {setLoading(false);}
			}
		};

		void fetchData();
		return () => {
			mounted = false;
		};
	}, [id, refreshTick]);

	useEffect(() => {
		const unsubscribe = subscribe(() => {
			setRefreshTick((prev) => prev + 1);
		});
		return unsubscribe;
	}, [subscribe]);

	useEffect(() => {
		return alarmAcknowledgedEvent.subscribe((payload) => {
			if (payload?.source === 'signalr' && payload?.eventType === 'BureauAlarmUpdated') {
				return;
			}
			setRefreshTick((prev) => prev + 1);
		});
	}, []);

	const categoryCounts = useMemo<Record<AlarmCategory, number>>(() => {
		const counts: Record<AlarmCategory, number> = {
			all: 0,
			'active-not-ack': 0,
			'active-ack': 0,
			'not-active-not-ack': 0,
		};

		alarms.forEach((alarm) => {
			const active = isActiveAlarm(alarm);
			const ack = toBool(alarm.localAck);

			counts.all += 1;
			if (active && !ack) {counts['active-not-ack'] += 1;}
			if (active && ack) {counts['active-ack'] += 1;}
			if (!active && !ack) {counts['not-active-not-ack'] += 1;}
		});

		return counts;
	}, [alarms]);

	const filteredByCategory = useMemo(() => {
		return alarms.filter((alarm) => {
			if (selectedCategory === 'all') {return true;}
			const active = isActiveAlarm(alarm);
			const ack = toBool(alarm.localAck);
			if (selectedCategory === 'active-not-ack') {return active && !ack;}
			if (selectedCategory === 'active-ack') {return active && ack;}
			return !active && !ack;
		});
	}, [alarms, selectedCategory]);

	const searched = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) {return filteredByCategory;}
		return filteredByCategory.filter((alarm) => {
			const fields = [
				alarm.siteName,
				alarm.siteIdentifier,
				alarm.alarmIdentifier,
				alarm.pathToAlarm,
				alarm.alarmMessage,
				alarm.bureauRecipientName,
			];
			return fields.some((value) => String(value || '').toLowerCase().includes(q));
		});
	}, [filteredByCategory, searchQuery]);

	const sorted = useMemo(() => {
		const arr = [...searched];
		const getString = (a: BureauAlarm, key: string): string => {
			if (key === 'siteName') {return String(a.siteName || '').toLowerCase();}
			if (key === 'siteIdentifier') {return String(a.siteIdentifier || '').toLowerCase();}
			if (key === 'recipient') {return String(a.bureauRecipientName || a.bureauName || '').toLowerCase();}
			if (key === 'path') {return String(a.pathToAlarm || a.path || a.alarmIdentifier || '').toLowerCase();}
			if (key === 'sourceAlarmState') {return String(a.sourceAlarmState || a.bureauAlarmState || '').toLowerCase();}
			if (key === 'alarmMessage') {return String(a.alarmMessage || '').toLowerCase();}
			return '';
		};
		const getDate = (a: BureauAlarm): number => (a.lastOccurrenceAt ? new Date(a.lastOccurrenceAt).getTime() : 0);

		arr.sort((a, b) => {
			let result = 0;
			if (sortColumn === 'last') {
				result = getDate(a) - getDate(b);
			} else if (sortColumn === 'ack') {
				result = Number(toBool(b.localAck)) - Number(toBool(a.localAck));
			} else if (sortColumn) {
				const left = getString(a, sortColumn);
				const right = getString(b, sortColumn);
				result = left.localeCompare(right);
			}
			return sortOrder === 'asc' ? result : -result;
		});
		return arr;
	}, [searched, sortColumn, sortOrder]);

	const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));

	useEffect(() => {
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, totalPages]);

	const pageData = useMemo(() => {
		const start = (page - 1) * PAGE_SIZE;
		return sorted.slice(start, start + PAGE_SIZE);
	}, [sorted, page]);

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
			return;
		}
		setSortColumn(column);
		setSortOrder('asc');
	};

	const handleClearFilter = () => {
		setSelectedCategory('active-not-ack');
		setPage(1);
		setIsFilterOpen(false);
	};

	if (loading) {
		return (
			<main>
				<div className="container">
					<section className="page">
						<h2 className="heading__title heading__title--building">Loading CSH details...</h2>
						<DateTimeDisplay />
					</section>
					<section className="page-section">
						<LoadingSpinner message="Loading CSH group..." />
					</section>
				</div>
			</main>
		);
	}

	if (error || !group) {
		return (
			<main>
				<div className="container">
					<section className="page-section">
						<h2>CSH Group Not Found</h2>
						<p>{error || 'The requested CSH group could not be found.'}</p>
						<button className="btn btn--secondary" onClick={() => navigate(-1)}>Back</button>
					</section>
				</div>
			</main>
		);
	}

	const isFilterActive = selectedCategory !== 'active-not-ack';

	return (
		<main>
			<div className="container">
				<section className="page">
					<div className="heading__section">
						<div className="heading__title-group">
							<h2 className="heading__title heading__title--segment-building">{group.name}</h2>
							<DateTimeDisplay />
							<Breadcrumbs items={breadcrumbs} />
						</div>
					</div>
				</section>

				{successMessage && (
					<StatusMessage kind="success" onClose={() => setSuccessMessage(null)}>
						{successMessage}
					</StatusMessage>
				)}

				<section className="alarm-content mb-2">
					<div className="toolbar-content pt-1 pb-1">
						<div className="toolbar-content__left">
							<h3 className="mb-1">CSH - {getBureauCategoryLabel(selectedCategory)}</h3>
							<div className="toolbar-content__search">
								<SearchQuery searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
							</div>
						</div>

						<div className="toolbar-content__right">
							<div className="alarm-controls">
								<div className="filter-container">
									<FilterBtn
										onClick={() => setIsFilterOpen((prev) => !prev)}
										isActive={isFilterActive}
										isDropdownOpen={isFilterOpen}
									/>

									<BureauFilter
										isOpen={isFilterOpen}
										onClose={() => setIsFilterOpen(false)}
										selectedCategory={selectedCategory}
										onCategoryChange={(cat) => {
											setSelectedCategory(cat as AlarmCategory);
											setPage(1);
										}}
										onClear={handleClearFilter}
										categoryCounts={categoryCounts}
									/>
								</div>
							</div>
						</div>
					</div>
				</section>

				<section className="mb-2">
					<BureauCategoryTable
						filteredData={pageData}
						totalCount={sorted.length}
						page={page}
						pageSize={PAGE_SIZE}
						selectedIds={selectedIds}
						onToggleSelect={(alarmId: number) => {
							setSelectedIds((prev) => ({ ...prev, [alarmId]: !prev[alarmId] }));
						}}
						onToggleSelectAll={() => {
							const allSelected = pageData.every((item) => selectedIds[(item.bureauAlarmId || item.id) ?? 0]);
							if (allSelected) {
								setSelectedIds({});
								return;
							}
							const next: Record<number, boolean> = {};
							pageData.forEach((item) => {
								const alarmId = (item.bureauAlarmId || item.id) ?? 0;
								if (alarmId) {next[alarmId] = true;}
							});
							setSelectedIds(next);
						}}
						onAcknowledged={(count?: number, acknowledgedAlarmIds?: number[]) => {
							if (acknowledgedAlarmIds && acknowledgedAlarmIds.length > 0) {
								const ackSet = new Set(acknowledgedAlarmIds);
								setAlarms((prev) => prev.map((alarm) => {
									const alarmId = alarm.bureauAlarmId || alarm.id;
									if (alarmId && ackSet.has(alarmId)) {
										return { ...alarm, localAck: true };
									}
									return alarm;
								}));
							}
							setSuccessMessage(`Acknowledged ${count ?? acknowledgedAlarmIds?.length ?? 1} alarm(s)`);
							setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
						}}
						sortColumn={sortColumn}
						sortOrder={sortOrder}
						onSort={handleSort}
						onResetSort={() => {
							setSortColumn('last');
							setSortOrder('desc');
							setPage(1);
						}}
						isLoading={loading}
					/>

					{totalPages > 1 && (
						<Pagination
							currentPage={page}
							totalPages={totalPages}
							onPageChange={setPage}
						/>
					)}
				</section>
			</div>
		</main>
	);
};

export default CSHDetails;
